import { useState } from 'react';
import PrimaryButton from './PrimaryButton';
import BottomChevron from '../icons/BottomChevron';
import { useDispatch, useSelector } from 'react-redux';
import {
  appendCompareProduct,
  removeCompareProduct,
  setIsKeyFeaturesExpanded,
  setProductDetails,
  updateFormData,
  updateSalesReqBody,
} from '../features/FormRegistration';
import {
  calculateSaving,
  formatCurrency,
  formatHtml,
} from '../utils/helpers';
import { toast } from 'react-toastify';
import { Checkbox, Loader, Modal, Select } from '@mantine/core';
import { CoverType } from '../types/types';
import { ShowIf } from './Helpers/ShowIf';
import GreenCheckIcon from '../icons/GreenCheckIcon';
import RestrictedIcon from '../icons/RestrictedIcon';
import RedCrossIcon from '../icons/RedCrossIcon';

interface Benefit {
  benefit_name: string;
  benefit_description: string;
  benefit_type: 'hospital' | 'extra';
  sis_type: string;
  waiting_period: string;
  policy_limit: string;
  coverage: string;
}

interface CoverComparisonProps {
  coverTitle: string;
  coverImage: string;
  coverMainPrice: number;
  coverMainPriceDecimal: number;
  lhcComponent: number;
  excess: string;
  coPayment: string;
  specialOfferTitle: string;
  specialOfferDescription: string;
  benefits: Benefit[];
  govRebateData?: {
    rebatePercentage: string;
    loading: number;
    isExtras: boolean;
  };
  description: string;
  brochureUrl: string;
  productId: number;
  healthFundId: number;
  expandedBenefits: Record<string, boolean>;
  onToggleDescription: (benefitName: string) => void;
  ambulance: string;
  productApiData: {
    key_features?: string;
    [key: string]: any;
  };
}

function CoverComparison({
  coverTitle,
  coverImage,
  coverMainPrice,
  coverMainPriceDecimal,
  lhcComponent,
  excess,
  specialOfferTitle,
  specialOfferDescription,
  coPayment,
  benefits,
  govRebateData,
  description,
  brochureUrl,
  productId,
  healthFundId,
  expandedBenefits,
  onToggleDescription,
  ambulance,
  productApiData,
}: CoverComparisonProps) {
  const dispatch = useDispatch();
  const [isLoading] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);

  const hospitalBenefits = benefits.filter(
    (b) => b.benefit_type === 'hospital',
  );

  const extraBenefits = benefits.filter((b) => b.benefit_type === 'extra');
  const { formData, compareProducts, salesReqBody, isKeyFeaturesExpanded } =
    useSelector((state: any) => state.formRegistration);


  const totalCoverMainPrice = (
    Number.parseFloat(`${coverMainPrice}.${coverMainPriceDecimal}`) +
    lhcComponent
  ).toFixed(2);
  const isPriceValid = !isNaN(Number.parseFloat(totalCoverMainPrice));
  const isCompareProduct = compareProducts.some(
    (product: any) => product.productId === productId,
  );

  const { isDesktopView, isMobileView } = useSelector(
    (state: any) => state.viewport,
  );
  const getSpecialOfferTag = () => {
    if (!specialOfferTitle) return null;
    // clean up special characters like ! @ etc.
    return specialOfferTitle.replace(/[^\w\s]/g, '');
  };
  const promoColor = productApiData?.policy_promo_tab_colour;
  const promoText = productApiData?.policy_promo_text;
  const MaintenanceModal = () => (
    <Modal
      opened={maintenanceOpen}
      onClose={() => setMaintenanceOpen(false)}
      centered
      title="Online Sign-Up Unavailable"
    >
      <div className="flex flex-col items-center gap-4 p-2 text-center">
        <p className="text-sm text-gray-700">
          Our online sign-up page is under maintenance, please call{' '}
          <a href="tel:1300369399" className="font-bold text-green-700 whitespace-nowrap">1300 369 399</a>{' '}
          and a Health Deal consultant will process the transfer for you in a matter of minutes.
        </p>
        <PrimaryButton
          text="Close"
          onClick={() => setMaintenanceOpen(false)}
          classNames="mt-2"
        />
      </div>
    </Modal>
  );

  if (isMobileView) {
    return (
      <div className="w-full">
        <div className="h-[30px]">
          {specialOfferTitle && (
            <div className="bg-[#B8DBF7] h-[30px] rounded-r-[10px] rounded-tl-[10px] w-[min-content] truncate px-[15px] py-[7px] flex items-center self-center justify-center">
              {getSpecialOfferTag()}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center border border-[#D1D7DF] relative w-full p-[10px] gap-[5px]">
          {/* Top row: Compare + Promo */}
          <div className="flex items-center justify-between w-full">
            <Checkbox
              label="Compare"
              className="self-start"
              onChange={(val) => {
                if (val.target.checked) {
                  if (compareProducts.length >= 2) {
                    toast.error('You can only compare up to 2 products');
                    return;
                  }
                  dispatch(
                    appendCompareProduct({
                      coverTitle,
                      coverImage,
                      coverMainPrice,
                      coverMainPriceDecimal,
                      lhcComponent,
                      excess,
                      specialOfferTitle,
                      specialOfferDescription,
                      coPayment,
                      benefits,
                      govRebateData,
                      description,
                      brochureUrl,
                      productId,
                      healthFundId,
                      expandedBenefits,
                      onToggleDescription,
                      ambulance,
                      productApiData,
                    }),
                  );
                } else {
                  dispatch(removeCompareProduct(productId));
                }
              }}
              checked={isCompareProduct}
            />
            <ShowIf condition={promoText}>
              <div
                style={{ backgroundColor: promoColor }}
                className="flex items-center px-[5px] py-[2px] rounded-md">
                <span
                  style={{ color: 'white' }}
                  className="text-[12px] py-[2px]">
                  {promoText}
                </span>
              </div>
            </ShowIf>
          </div>

          {/* Logo + Title + Price in compact layout */}
          <img
            src={coverImage}
            alt="health fund logo"
            className="w-[80px] h-[40px]"
          />
          <span className="text-[13px] font-bold w-full text-center leading-tight">
            {coverTitle.replace(/&amp;/g, '-')}
          </span>
          {isPriceValid ? (
            <span className="text-[16px] font-medium text-center w-full">
              <span className="font-[600]">$</span>
              <span className="text-[22px] font-[600]">
                {totalCoverMainPrice.toString().split('.')[0]}
              </span>
              <span className="font-[600] text-[12px]">
                .{totalCoverMainPrice.toString().split('.')[1]}
              </span>
            </span>
          ) : (
            <span className="text-[18px] flex items-center justify-center relative font-medium h-[40px]">
              Not available
            </span>
          )}
          <Select
            data={['Fortnightly', 'Monthly', 'Annual']}
            size="xs"
            classNames={{
              input: 'text-center',
            }}
            value={formData.paymentFrequency}
            onChange={(e) => {
              dispatch(updateFormData({ ...formData, paymentFrequency: e }));
              dispatch(
                updateSalesReqBody({
                  ...salesReqBody,
                  payment: {
                    ...salesReqBody.payment,
                    payment_period: e === 'Annual' ? 'Yearly' : e,
                    payment_date: null,
                  },
                }),
              );
            }}
          />
          {(formData.typeOfCover === CoverType.HospitalAndExtras ||
            formData.typeOfCover === CoverType.Hospital) && (
            <ShowIf condition={lhcComponent > 0}>
              <span className="text-start text-[11px]">
                <strong>
                  {formatCurrency(
                    Number.parseFloat(
                      `${coverMainPrice}.${coverMainPriceDecimal}`,
                    ),
                  )}
                </strong>{' '}
                + <strong>{formatCurrency(lhcComponent)}</strong> Lifetime
                Health Cover Loading
              </span>
            </ShowIf>
          )}
          {govRebateData && (
            <span className="text-[11px] text-start">
              Includes{' '}
              <strong>
                {govRebateData.rebatePercentage}%
              </strong>{' '}
              Government Rebate
              {!govRebateData.isExtras && govRebateData.loading > 0 && (
                <> + <strong>{govRebateData.loading}%</strong> loading</>
              )}
            </span>
          )}
          {isLoading ? (
            <Loader />
          ) : (
            <div className="w-full">
              <MaintenanceModal />
              <PrimaryButton
                style={{ marginBottom: 8 }}
                classNames="w-full"
                text="Get Covered"
                bgColor={'bg-[#007500]'}
                onClick={() => setMaintenanceOpen(true)}
              />
              <span className="text-[11px] text-start float-right">
                  Take less than 2 minutes
                </span>
              </div>
            )}
          <span
            className="underline mb-[15px] cursor-pointer font-[500]"
            onClick={() =>
              dispatch(
                setProductDetails({
                  coverTitle,
                  coverImage,
                  coverMainPrice,
                  coverMainPriceDecimal,
                  excess,
                  coPayment,
                  specialOfferTitle,
                  specialOfferDescription,
                  benefits,
                  govRebateData,
                  healthFundId,
                  description,
                  productId,
                  brochureUrl,
                  ambulance,
                }),
              )
            }>
            View details
          </span>
          <div className="relative w-full mb-[10px]">
            {specialOfferTitle ? (
              <div className="w-full bg-[#F6F6F6] py-[10px] min-h-[80px] border-t-[1px] border-b-[1px] border-[#D9D9D9]">
                <span className="text-[15px] font-bold">
                  {specialOfferTitle}
                </span>
                {specialOfferDescription && (
                  <p
                    className="text-[12px] font-normal px-[10px] text-center [&_li]:list-disc [&_li]:ml-5"
                    dangerouslySetInnerHTML={{
                      __html: formatHtml(specialOfferDescription),
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="bg-[#E7F3FC] w-full py-[10px] min-h-[80px] flex items-center justify-center"></div>
            )}
            {calculateSaving(
              formData.previousPayment,
              Number(totalCoverMainPrice),
              formData.paymentFrequency,
            ) !== '0.00' && (
              <div className="bg-[#FFF5E5] p-[10px] flex gap-[5px] justify-center">
                <span className="text-[14px] font-bold">Save</span>
                <span className="text-[9px] font-[400]">up to</span>
                <span>
                  <span className="text-[14px] font-bold">
                    {formatCurrency(
                      Number.parseFloat(
                        calculateSaving(
                          formData.previousPayment,
                          Number(totalCoverMainPrice),
                          formData.paymentFrequency,
                        ),
                      ),
                    )}
                  </span>
                  <span className="text-[10px] font-[400]">/year</span>
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col w-full">
            <div className="flex justify-between w-full px-[15px] mb-[19px]">
              <span className="font-bold text-[12px]">Excess</span>
              <span className="font-bold text-[12px]">
                {formatCurrency(Number.parseFloat(excess))}
              </span>
            </div>
            <div className="flex justify-between w-full px-[15px] mb-[20px]">
              <span className="font-bold text-[12px]">Co payment</span>
              <span className="font-bold text-[12px]">{coPayment}</span>
            </div>
          </div>
          <div className="border-t-2 w-full border-[#D1D7DF] mb-[10px]"></div>
          <div className="flex flex-col w-full">
            <div className="min-h-[35px] w-full">
              {productApiData?.key_features && (
                <>
                  <div className="flex flex-col w-full">
                    <div
                      className="flex cursor-pointer justify-between items-center w-full"
                      onClick={() => {
                        
                        dispatch(
                          setIsKeyFeaturesExpanded(!isKeyFeaturesExpanded),
                        );
                      }}>
                      <span className="text-[14px] font-bold text-start">
                        Key Features
                      </span>
                      <div
                        className={`transition-transform duration-300 ${
                          isKeyFeaturesExpanded
                            ? 'rotate-[-180deg]'
                            : 'rotate-0'
                        }`}>
                        <BottomChevron />
                      </div>
                    </div>
                    <div
                      className={`overflow-hidden transition-max-height duration-500 ease-in-out w-full ${
                        isKeyFeaturesExpanded ? 'max-h-[500px]' : 'max-h-0'
                      }`}>
                      <div
                        className="text-[14px] text-start mt-2 [&_ul]:list-none [&_ul]:pl-0 [&_li]:flex [&_li]:items-start [&_li]:gap-2 [&_li]:mb-2 [&_li]:before:content-[''] [&_li]:before:inline-block [&_li]:before:w-4 [&_li]:before:h-4 [&_li]:before:min-w-4 [&_li]:before:flex-shrink-0 [&_li]:before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj48cGF0aCBkPSJNMTMuMzMzNyA0TDYuMDAwMzMgMTEuMzMzM0wyLjY2Njk5IDgiIHN0cm9rZT0iIzAwNzUwMCIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjwvcGF0aD48L3N2Zz4=')] [&_li]:before:bg-no-repeat [&_li]:before:bg-contain"
                        dangerouslySetInnerHTML={{
                          __html: formatHtml(productApiData.key_features),
                        }}></div>
                    </div>
                    <div className="border-t-2 w-full border-[#D1D7DF] mt-4"></div>
                  </div>
                </>
              )}
            </div>
          </div>
          <BenefitSection
            title="Hospital Benefits"
            benefits={hospitalBenefits}
            expandedBenefits={expandedBenefits}
            onToggle={onToggleDescription}
          />
          <div className="border-t-2 w-full border-[#D1D7DF] mt-4"></div>
          <BenefitSection
            title="Extras Cover"
            benefits={extraBenefits}
            expandedBenefits={expandedBenefits}
            onToggle={onToggleDescription}
          />
          <div className="border-t-2 w-full border-[#D1D7DF] mt-4"></div>
          {ambulance && (
            <div className="flex flex-col px-[15px] text-start items-start w-full mb-[36px]">
              <span className="text-[17px] font-medium mb-[10px]">
                Ambulance Cover
              </span>
              <p
                className="text-[12px]"
                dangerouslySetInnerHTML={{
                  __html: formatHtml(ambulance),
                }}></p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isDesktopView) {
    return (
      <div className="w-full">
        <div
          className={`${
            specialOfferTitle ? 'bg-[#B8DBF7]' : 'bg-transparent'
          } h-[30px] rounded-r-[10px] rounded-tl-[10px] w-[min-content] truncate px-[15px] py-[7px] flex items-center`}>
          {getSpecialOfferTag()}
        </div>

        <div className="flex flex-col items-center border border-[#D1D7DF] relative w-full p-[10px] md:gap-[8px] gap-[5px] pb-4">
          <div className="flex items-center justify-between w-full flex-wrap gap-2">
            <Checkbox
              label="Compare"
              className="self-start"
              onChange={(val) => {
                if (val.target.checked) {
                  if (compareProducts.length >= 4) {
                    toast.error('You can only compare up to 4 products');
                    return;
                  }
                  dispatch(
                    appendCompareProduct({
                      coverTitle,
                      coverImage,
                      coverMainPrice,
                      coverMainPriceDecimal,
                      lhcComponent,
                      excess,
                      specialOfferTitle,
                      specialOfferDescription,
                      coPayment,
                      benefits,
                      govRebateData,
                      description,
                      brochureUrl,
                      productId,
                      healthFundId,
                      expandedBenefits,
                      onToggleDescription,
                      ambulance,
                      productApiData,
                    }),
                  );
                } else {
                  dispatch(removeCompareProduct(productId));
                }
              }}
              checked={isCompareProduct}
            />
            <ShowIf condition={promoText}>
              <div
                className="rounded-md px-[10px] flex"
                style={{ backgroundColor: promoColor }}>
                <span
                  style={{ color: 'white' }}
                  className="text-[12px] py-[2px]">
                  {promoText}
                </span>
              </div>
            </ShowIf>
          </div>

          <img src={coverImage} alt="health fund logo" className=" h-[50px]" />
          <span className="text-[14px] font-bold w-full min-h-[40px] text-center">
            {coverTitle.replace(/&amp;/g, '-')}
          </span>
          {isPriceValid ? (
            <span className="text-[28px] relative font-bold text-center w-full">
              {formatCurrency(Number(totalCoverMainPrice))}
            </span>
          ) : (
            <span className="text-[24px] flex items-center justify-center relative font-medium h-[50px]">
              Not available
            </span>
          )}
          <Select
            data={['Fortnightly', 'Monthly', 'Annual']}
            className="w-full"
            classNames={{
              input: 'text-center',
            }}
            value={formData.paymentFrequency}
            onChange={(e) => {
              dispatch(updateFormData({ ...formData, paymentFrequency: e }));
              dispatch(
                updateSalesReqBody({
                  ...salesReqBody,
                  payment: {
                    ...salesReqBody.payment,
                    payment_period: e === 'Annual' ? 'Yearly' : e,
                    payment_date: null,
                  },
                }),
              );
            }}
          />

          {isLoading ? (
            <Loader />
          ) : (
            <div className="w-full">
              <MaintenanceModal />
              <PrimaryButton
                style={{ marginBottom: 23 }}
                classNames="w-full"
                bgColor={'bg-[#007500]'}
                text="Get Covered"
                onClick={() => setMaintenanceOpen(true)}
              />
              <span className="text-[12px] text-start -mt-[20px] float-right">
                Take less than 2 minutes
              </span>
            </div>
          )}
          {(formData.typeOfCover === CoverType.HospitalAndExtras ||
            formData.typeOfCover === CoverType.Hospital) && (
            <ShowIf condition={lhcComponent > 0}>
              <span className="text-start text-[12px]">
                <strong>
                  {formatCurrency(
                    Number.parseFloat(
                      `${coverMainPrice}.${coverMainPriceDecimal}`,
                    ),
                  )}
                </strong>{' '}
                + <strong>{formatCurrency(lhcComponent)}</strong> Lifetime
                Health Cover Loading
              </span>
            </ShowIf>
          )}
                     {govRebateData && (
              <div className="self-start">
                <span className="md:text-[14px] text-[12px] text-start">
                  Includes{' '}
                  <strong>
                    {govRebateData.rebatePercentage}%
                  </strong>{' '}
                  Government Rebate
                  {!govRebateData.isExtras && govRebateData.loading > 0 && (
                    <> + <strong>{govRebateData.loading}%</strong> loading</>
                  )}
                </span>
              </div>
            )}
          <span
            className="underline mb-[15px] cursor-pointer text-start font-[500]"
            onClick={() =>
              dispatch(
                setProductDetails({
                  coverTitle,
                  coverImage,
                  coverMainPrice,
                  coverMainPriceDecimal,
                  excess,
                  coPayment,
                  specialOfferTitle,
                  specialOfferDescription,
                  benefits,
                  govRebateData,
                  healthFundId,
                  description,
                  productId,
                  brochureUrl,
                  ambulance,
                }),
              )
            }>
            View details
          </span>

          <div className="relative w-full mb-[10px]">
            {specialOfferTitle ? (
              <div className="w-full bg-[#F6F6F6] py-[10px] min-h-[80px] border-t-[1px] border-b-[1px] border-[#D9D9D9]">
                <span className="text-[15px] font-bold">
                  {specialOfferTitle}
                </span>
                {specialOfferDescription && (
                  <p
                    className="text-[12px] font-normal px-[10px] text-center [&_li]:list-disc [&_li]:ml-5"
                    dangerouslySetInnerHTML={{
                      __html: formatHtml(specialOfferDescription),
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="bg-[#E7F3FC] w-full py-[10px] min-h-[80px] flex items-center justify-center"></div>
            )}
            {calculateSaving(
              formData.previousPayment,
              Number(totalCoverMainPrice),
              formData.paymentFrequency,
            ) !== '0.00' && (
              <div className="bg-[#FFF5E5] p-[10px] flex gap-[5px] justify-center">
                <span className="text-[14px] font-bold">Save</span>
                <span className="text-[9px] font-[400]">up to</span>
                <span>
                  <span className="text-[14px] font-bold">
                    {formatCurrency(
                      Number.parseFloat(
                        calculateSaving(
                          formData.previousPayment,
                          Number(totalCoverMainPrice),
                          formData.paymentFrequency,
                        ),
                      ),
                    )}
                  </span>
                  <span className="text-[10px] font-[400]">/year</span>
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col w-full">
            <div className="flex justify-between w-full px-[15px] mb-[19px]">
              <span className="font-bold text-[14px]">Excess</span>
              <span className="font-bold text-[14px]">
                {formatCurrency(Number.parseFloat(excess))}
              </span>
            </div>
            <div className="flex justify-between w-full px-[15px] mb-[20px]">
              <span className="font-bold text-[14px]">Co payment</span>
              <span className="font-bold text-[14px]">{coPayment}</span>
            </div>
          </div>

          <div className="border-t-2 w-full border-[#D1D7DF] "></div>
          <div className="min-h-[35px] w-full">
            {productApiData?.key_features && (
              <>
                <div className="flex flex-col w-full">
                  <div
                    className="flex cursor-pointer justify-between items-center w-full"
                    onClick={() => {
                      
                      
                      dispatch(
                        setIsKeyFeaturesExpanded(!isKeyFeaturesExpanded),
                      );
                    }}>
                    <span className="text-[14px] font-bold text-start">
                      Key Features
                    </span>
                    <div
                      className={`transition-transform duration-300 ${
                        isKeyFeaturesExpanded ? 'rotate-[-180deg]' : 'rotate-0'
                      }`}>
                      <BottomChevron />
                    </div>
                  </div>
                  <div
                    className={`overflow-hidden transition-max-height duration-500 ease-in-out w-full ${
                      isKeyFeaturesExpanded ? 'max-h-[500px]' : 'max-h-0'
                    }`}>
                    <div
                      className="text-[14px] text-start mt-2 [&_ul]:list-none [&_ul]:pl-0 [&_li]:flex [&_li]:items-start [&_li]:gap-2 [&_li]:mb-2 [&_li]:before:content-[''] [&_li]:before:inline-block [&_li]:before:w-4 [&_li]:before:h-4 [&_li]:before:min-w-4 [&_li]:before:flex-shrink-0 [&_li]:before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj48cGF0aCBkPSJNMTMuMzMzNyA0TDYuMDAwMzMgMTEuMzMzM0wyLjY2Njk5IDgiIHN0cm9rZT0iIzAwNzUwMCIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjwvcGF0aD48L3N2Zz4=')] [&_li]:before:bg-no-repeat [&_li]:before:bg-contain"
                      dangerouslySetInnerHTML={{
                        __html: formatHtml(productApiData.key_features),
                      }}></div>
                  </div>
                  <div className="border-t-2 w-full border-[#D1D7DF] mt-4"></div>
                </div>
              </>
            )}
          </div>

          <BenefitSection
            title="Hospital Benefits"
            benefits={hospitalBenefits}
            expandedBenefits={expandedBenefits}
            onToggle={onToggleDescription}
          />
          <div className="border-t-2 w-full border-[#D1D7DF] mt-8"></div>
          <BenefitSection
            title="Extras Cover"
            benefits={extraBenefits}
            expandedBenefits={expandedBenefits}
            onToggle={onToggleDescription}
          />
          <div className="border-t-2 w-full border-[#D1D7DF] mt-8"></div>
          {ambulance && (
            <div className="flex flex-col px-[15px] text-start items-start w-full mb-[36px]">
              <span className="text-[17px] font-medium mb-[10px]">
                Ambulance Cover
              </span>
              <p
                className="text-[12px]"
                dangerouslySetInnerHTML={{
                  __html: formatHtml(ambulance),
                }}></p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

interface BenefitSectionProps {
  title: string;
  benefits: Benefit[];
  expandedBenefits: Record<string, boolean>;
  onToggle: (benefitName: string) => void;
}

export const BenefitSection = ({
  title,
  benefits,
  expandedBenefits,
  onToggle,
}: BenefitSectionProps) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(true);

  if (benefits.length > 0)
    return (
      <>
        <div className="flex flex-col text-start items-start w-full -mb-[35px]">
          <div
            className="flex justify-between items-center w-full cursor-pointer mb-[19px]"
            onClick={() => setIsSectionExpanded(!isSectionExpanded)}>
            <span className="text-[17px] font-medium">{title}</span>
            <div
              className={`transform transition-transform duration-300 ${isSectionExpanded ? 'rotate-180' : ''}`}>
              <BottomChevron />
            </div>
          </div>
          <div
            className={`flex flex-col gap-[30px] w-full transition-all duration-300 ${isSectionExpanded ? 'opacity-100 ' : 'opacity-0 max-h-0 overflow-hidden'}`}>
            {benefits.map((benefit, index) => {
              let icon;

              switch (benefit.sis_type) {
                case 'Hospital':
                case 'Extra':
                  icon = <GreenCheckIcon />;
                  break;
                case 'H_RESTRICTION':
                case 'E_RESTRICTION':
                  icon = <RestrictedIcon />;
                  break;
                case 'E_EXCLUSION':
                case 'H_EXCLUSION':
                  icon = <RedCrossIcon />;
                  break;

                default:
                  icon = <div></div>;
                  break;
              }

              return (
                <div key={index} className="flex flex-col gap-[5px] w-full">
                  <div
                    className="flex justify-between w-full cursor-pointer"
                    onClick={() => onToggle(benefit.benefit_name)}>
                    <div className="flex gap-[5px] w-full flex-grow font-bold overflow-hidden">
                      <div className="w-[20px]">{icon}</div>
                      <span className="text-[12px] w-full">
                        {benefit.benefit_name}
                      </span>
                    </div>
                    <div>
                      <BottomChevron />
                    </div>
                  </div>
                  <div
                    className={`overflow-hidden transition-max-height duration-500 text-[13.5px] ease-in-out w-full ${
                      expandedBenefits[benefit.benefit_name]
                        ? 'max-h-auto'
                        : 'max-h-0'
                    }`}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: formatHtml(benefit.benefit_description),
                      }}></p>

                    {benefit.coverage && (
                      <div>
                        <b className="font-bold">Coverage:</b>{' '}
                        <p
                          dangerouslySetInnerHTML={{
                            __html: formatHtml(benefit.coverage),
                          }}></p>
                      </div>
                    )}

                    {benefit.waiting_period && (
                      <div>
                        <b className="font-bold">Waiting period:</b>{' '}
                        <p
                          dangerouslySetInnerHTML={{
                            __html: formatHtml(benefit.waiting_period),
                          }}></p>
                      </div>
                    )}
                    {benefit.policy_limit && (
                      <div>
                        <b className="font-bold">Yearly Limit:</b>{' '}
                        <p
                          dangerouslySetInnerHTML={{
                            __html: formatHtml(benefit.policy_limit),
                          }}></p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
};

export default CoverComparison;
